// Labels of Administration › Datasets: the version build states with their pill tone and icon (Ready is a build state, Published a lifecycle status; the two never share a word), section extract kinds, jurisdiction, authority, document and licence kinds, readers, language support, profile states, audit actions as sentences (capability documents, assessments and topic reviews included) with their filter groups, relative times and the euro cost cap with its USD equivalent.
import type { LucideIcon } from "lucide-react";
import {
  CircleCheckIcon,
  CircleDashedIcon,
  CircleXIcon,
  ClockIcon,
  LoaderCircleIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from "lucide-react";

import type {
  AuthorityKind,
  CostCap,
  DatasetAction,
  DocType,
  JurisdictionKind,
  LanguageSupport,
  LicenceClass,
  ProfileStatus,
  VersionBuild,
} from "@/api/__generated__";
import { formatDateTime, formatNumber } from "@/lib/format";

export type BuildStateName = VersionBuild["state"];
type Tone = "ok" | "grey" | "attn" | "brand" | "neutral" | "outline";

export interface BuildStateLabel {
  label: string;
  variant: Tone;
  icon: LucideIcon;
  spinning?: boolean;
}

export const BUILD_STATES: readonly BuildStateName[] = [
  "not_built",
  "queued",
  "building",
  "interrupted",
  "failed",
  "ready",
  "deleting",
];

export const BUILD_STATE_LABELS: Record<BuildStateName, BuildStateLabel> = {
  not_built: { label: "Not built", variant: "outline", icon: CircleDashedIcon },
  queued: { label: "Queued", variant: "grey", icon: ClockIcon },
  building: { label: "Building", variant: "brand", icon: LoaderCircleIcon, spinning: true },
  interrupted: { label: "Interrupted", variant: "grey", icon: TriangleAlertIcon },
  failed: { label: "Failed", variant: "attn", icon: CircleXIcon },
  ready: { label: "Ready", variant: "ok", icon: CircleCheckIcon },
  deleting: { label: "Deleting", variant: "grey", icon: Trash2Icon, spinning: true },
};

export const EXTRACT_LABELS: Record<string, string> = {
  requirements: "Requirements",
  definitions: "Definitions",
  none: "Not extracted",
};

export const JURISDICTION_KIND_LABELS: Record<JurisdictionKind, string> = {
  union: "Union",
  country: "Country",
  region: "Region",
  synchronous_area: "Synchronous area",
  market: "Market",
};

export const AUTHORITY_KIND_LABELS: Record<AuthorityKind, string> = {
  eu_body: "EU body",
  regulator: "Regulator",
  system_operator: "System operator",
  tso: "Transmission system operator",
  dso: "Distribution system operator",
  standards_body: "Standards body",
  other: "Other",
};

export const DOC_TYPE_LABELS: Record<DocType, string> = {
  eu_regulation: "EU regulation",
  national_code: "National grid code",
  technical_rule: "Technical rule",
  operator_requirement: "Operator requirement",
  guidance: "Guidance",
  standard: "Standard",
  other: "Other",
};

export const LICENCE_LABELS: Record<LicenceClass, { label: string; description: string }> = {
  public: { label: "Public", description: "Every signed-in reader sees it." },
  licensed: { label: "Licensed", description: "Only readers with the licensed role see it." },
  confidential: { label: "Confidential", description: "Only readers with the confidential role see it." },
};

export const ADAPTER_LABELS: Record<string, string> = {
  generic: "Generic profile",
  neso_gb: "GB adapter",
};

export const PROFILE_STATE_LABELS: Record<ProfileStatus["state"], { label: string; variant: Tone }> = {
  none: { label: "Not profiled", variant: "outline" },
  queued: { label: "Profile queued", variant: "grey" },
  running: { label: "Reading layout", variant: "brand" },
  ready: { label: "Profiled", variant: "ok" },
  failed: { label: "Profile failed", variant: "attn" },
};

export function languageSupportLabel(support: Pick<LanguageSupport, "obligation_scan">): string {
  return support.obligation_scan === "native" ? "Native" : "Via English";
}

export function adapterLabel(adapter: string): string {
  return ADAPTER_LABELS[adapter] ?? adapter;
}

const ACTION_VERBS: Partial<Record<DatasetAction, string>> = {
  "registry.seeded": "seeded",
  "registry.jurisdiction.created": "added jurisdiction",
  "registry.jurisdiction.updated": "edited jurisdiction",
  "registry.jurisdiction.retired": "retired jurisdiction",
  "registry.authority.created": "added authority",
  "registry.authority.updated": "edited authority",
  "registry.authority.retired": "retired authority",
  "registry.family.created": "added grid code",
  "registry.family.updated": "edited grid code",
  "registry.family.retired": "retired grid code",
  "registry.family.sections_replaced": "changed the sections of",
  "registry.version.created": "added version",
  "registry.version.updated": "edited version",
  "registry.version.retired": "retired version",
  "source.uploaded": "uploaded",
  "source.deduplicated": "re-used the stored file",
  "source.profiled": "profiled",
  "upload.refused": "uploaded a file the PDF check refused:",
  "job.requested": "requested a job on",
  "job.cancelled": "cancelled a job on",
  "job.auto_resumed": "resumed a build of",
  "job.finished": "finished a job on",
  "document.ready": "released",
  "document.promoted": "promoted",
  "document.status_changed": "changed the status of",
  "document.deleted": "deleted",
  "run.rolled_back": "rolled back",
  "capability_document.uploaded": "uploaded the capability document",
  "capability_document.extracted": "read the capability document",
  "capability_document.deleted": "deleted the capability document",
  "assessment.created": "started the assessment",
  "assessment.finished": "finished the assessment",
  "assessment.failed": "could not finish the assessment",
  "assessment.approved": "approved the assessment",
  "assessment.reopened": "reopened the assessment",
  "assessment.deleted": "deleted the assessment",
  "topic.approved": "approved topic",
  "topic.changed": "changed topic",
  "topic.reset": "reset the review of topic",
  "settings.updated": "changed",
};

export const ACTION_GROUPS: readonly { key: string; label: string; actions: readonly DatasetAction[] }[] = [
  {
    key: "registry",
    label: "Registry",
    actions: [
      "registry.seeded",
      "registry.jurisdiction.created",
      "registry.jurisdiction.updated",
      "registry.jurisdiction.retired",
      "registry.authority.created",
      "registry.authority.updated",
      "registry.authority.retired",
      "registry.family.created",
      "registry.family.updated",
      "registry.family.retired",
      "registry.family.sections_replaced",
      "registry.version.created",
      "registry.version.updated",
      "registry.version.retired",
    ],
  },
  {
    key: "files",
    label: "Files",
    actions: ["source.uploaded", "source.deduplicated", "source.profiled", "upload.refused"],
  },
  {
    key: "jobs",
    label: "Builds",
    actions: ["job.requested", "job.cancelled", "job.auto_resumed", "job.finished"],
  },
  {
    key: "lifecycle",
    label: "Lifecycle",
    actions: [
      "document.ready",
      "document.promoted",
      "document.status_changed",
      "document.deleted",
      "run.rolled_back",
    ],
  },
  {
    key: "capability_documents",
    label: "Capability documents",
    actions: ["capability_document.uploaded", "capability_document.extracted", "capability_document.deleted"],
  },
  {
    key: "assessments",
    label: "Assessments",
    actions: [
      "assessment.created",
      "assessment.finished",
      "assessment.failed",
      "assessment.approved",
      "assessment.reopened",
      "assessment.deleted",
      "topic.approved",
      "topic.changed",
      "topic.reset",
    ],
  },
  { key: "settings", label: "Settings", actions: ["settings.updated"] },
];

export function actionVerb(action: DatasetAction): string {
  return ACTION_VERBS[action] ?? action;
}

const RELATIVE = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });
const UNITS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 86_400],
  ["month", 30 * 86_400],
  ["day", 86_400],
  ["hour", 3_600],
  ["minute", 60],
];

export function relativeTime(value: string | null | undefined, now: number = Date.now()): string {
  if (value === null || value === undefined) {
    return "–";
  }
  const seconds = Math.round((Date.parse(value) - now) / 1000);
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) {
      return RELATIVE.format(Math.round(seconds / size), unit);
    }
  }
  return "just now";
}

export function absoluteTime(value: string | null | undefined): string {
  return formatDateTime(value ?? null);
}

export function formatCostCap(cap: CostCap): string {
  const amount = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: cap.currency,
    maximumFractionDigits: 0,
  }).format(cap.amount);
  if (cap.currency === "USD") {
    return amount;
  }
  return `${amount} (≈ $${formatNumber(cap.usd)})`;
}

export function shortSha(sha256: string): string {
  return sha256.slice(0, 12);
}
